import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, RefreshCw, Inbox, TrendingUp, CheckCircle2, Clock } from "lucide-react";
import { listInquiries, updateInquiryStatus, adminStats } from "@/lib/api";

const STATUSES = ["NEW", "IN_PROGRESS", "QUOTED", "CLOSED"];
const STATUS_COLORS = {
  NEW: "bg-[#FF5238] text-white",
  IN_PROGRESS: "bg-amber-100 text-amber-800 border border-amber-200",
  QUOTED: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  CLOSED: "bg-slate-200 text-slate-700",
};

export default function Admin() {
  // sessionStorage instead of localStorage: token cleared when tab closes,
  // reducing XSS exfil window. Production should migrate to httpOnly cookie + JWT.
  const [token, setToken] = useState(sessionStorage.getItem("trawell_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("ALL");

  const load = async (t) => {
    setLoading(true);
    try {
      const [rows, s] = await Promise.all([listInquiries(t), adminStats(t)]);
      setInquiries(rows);
      setStats(s);
      setAuthed(true);
      sessionStorage.setItem("trawell_admin_token", t);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Invalid admin token");
        setAuthed(false);
        sessionStorage.removeItem("trawell_admin_token");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // load() captures token via closure; intentional one-shot mount effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    load(token.trim());
  };

  const handleStatus = async (id, status) => {
    try {
      await updateInquiryStatus(id, status, token);
      toast.success(`Marked as ${status}`);
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      const s = await adminStats(token);
      setStats(s);
    } catch {
      toast.error("Update failed");
    }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 md:py-32" data-testid="admin-login">
        <div className="overline mb-4">Agent Dashboard</div>
        <h1 className="font-heading text-4xl font-light tracking-tight text-[#0B192C]">
          Sign in.
        </h1>
        <p className="text-slate-600 mt-3 text-sm leading-relaxed">
          Enter your admin access token. The default (for demo): <code className="bg-slate-100 px-1.5 py-0.5 text-xs">trawell-admin-2026</code>
        </p>
        <form onSubmit={handleLogin} className="mt-8 space-y-4">
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            data-testid="admin-token-input"
            className="w-full bg-white border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#FF5238]"
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="btn-primary w-full justify-center"
            disabled={loading}
          >
            <LogIn className="h-4 w-4" /> Access dashboard
          </button>
        </form>
      </div>
    );
  }

  const filtered = filter === "ALL" ? inquiries : inquiries.filter((i) => i.status === filter);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24" data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <div className="overline mb-3">Agent Dashboard</div>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Inquiries & quotes
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => load(token)}
            disabled={loading}
            data-testid="admin-refresh-btn"
            className="btn-ghost border border-slate-200 text-sm !py-2.5 !px-4 flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => {
              sessionStorage.removeItem("trawell_admin_token");
              setAuthed(false);
              setToken("");
            }}
            data-testid="admin-logout-btn"
            className="text-sm text-slate-500 hover:text-[#FF5238]"
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
          <StatBox icon={Inbox} label="Total" value={stats.total_inquiries} />
          <StatBox icon={TrendingUp} label="New" value={stats.by_status?.NEW ?? 0} accent />
          <StatBox icon={Clock} label="In Progress" value={stats.by_status?.IN_PROGRESS ?? 0} />
          <StatBox icon={CheckCircle2} label="Quoted" value={stats.by_status?.QUOTED ?? 0} />
          <StatBox icon={TrendingUp} label="AI Sessions" value={stats.ai_sessions ?? 0} />
        </div>
      )}

      {/* Insights */}
      {stats && stats.total_inquiries > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10" data-testid="admin-insights">
          <BarPanel
            title="Top Destinations"
            subtitle={`Last ${stats.recent_7d ?? 0} inquiries in 7 days`}
            rows={stats.top_destinations ?? []}
            keyFormatter={(k) => String(k).replace(/-/g, " ")}
          />
          <BarPanel
            title="Inquiry Mix"
            subtitle="By type"
            rows={stats.by_type ?? []}
            keyFormatter={(k) => String(k)}
          />
          <BarPanel
            title="Popular Nights"
            subtitle="Trip duration demand"
            rows={(stats.by_nights ?? []).map((r) => ({ ...r, key: `${r.key}N` }))}
          />
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {["ALL", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`admin-filter-${s.toLowerCase()}`}
            className={`text-xs font-heading font-medium px-4 py-2 tracking-wide transition-colors ${
              filter === s
                ? "bg-[#0B192C] text-white"
                : "bg-white border border-slate-200 text-[#0B192C] hover:border-[#FF5238]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Inquiries table */}
      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="admin-inquiries-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#FDFCF8] border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <th className="text-left p-4">Contact</th>
              <th className="text-left p-4">Type</th>
              <th className="text-left p-4">Destination</th>
              <th className="text-left p-4">Nights</th>
              <th className="text-left p-4">Pax</th>
              <th className="text-left p-4">Message</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="p-10 text-center text-slate-500">
                  {loading ? "Loading…" : "No inquiries yet."}
                </td>
              </tr>
            )}
            {filtered.map((i) => (
              <tr key={i.id} className="border-b border-slate-100 align-top hover:bg-[#FDFCF8]/60">
                <td className="p-4">
                  <div className="font-heading font-semibold text-[#0B192C]">{i.name}</div>
                  <div className="text-xs text-slate-500">{i.email}</div>
                  {i.phone && <div className="text-xs text-slate-500">{i.phone}</div>}
                  {i.company && <div className="text-xs text-slate-400 mt-1">{i.company}</div>}
                </td>
                <td className="p-4"><span className="text-xs font-semibold">{i.inquiry_type}</span></td>
                <td className="p-4 capitalize">{i.destination || "—"}</td>
                <td className="p-4">{i.nights || "—"}</td>
                <td className="p-4">{i.pax || "—"}</td>
                <td className="p-4 max-w-xs">
                  <div className="text-xs text-slate-700 leading-relaxed line-clamp-3">{i.message}</div>
                  {i.travel_date && (
                    <div className="text-[11px] text-slate-400 mt-1">Travel: {i.travel_date}</div>
                  )}
                </td>
                <td className="p-4">
                  <span className={`inline-block text-[10px] font-semibold tracking-wider px-2.5 py-1 ${STATUS_COLORS[i.status]}`}>
                    {i.status}
                  </span>
                  <div className="text-[11px] text-slate-400 mt-2">
                    {new Date(i.created_at).toLocaleDateString()}
                  </div>
                </td>
                <td className="p-4">
                  <select
                    value={i.status}
                    onChange={(e) => handleStatus(i.id, e.target.value)}
                    data-testid={`admin-status-select-${i.id}`}
                    className="text-xs border border-slate-200 px-2 py-1.5 bg-white outline-none focus:border-[#FF5238]"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, accent }) {
  return (
    <div className={`border border-slate-200 bg-white p-5 ${accent ? "ring-1 ring-[#FF5238]/30" : ""}`}>
      <Icon className={`h-5 w-5 ${accent ? "text-[#FF5238]" : "text-slate-400"}`} />
      <div className="font-heading text-3xl font-light text-[#0B192C] mt-3 tracking-tight">{value}</div>
      <div className="overline mt-1 !text-slate-500">{label}</div>
    </div>
  );
}

function BarPanel({ title, subtitle, rows, keyFormatter }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-slate-200 bg-white p-6" data-testid={`bar-panel-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="overline">{title}</div>
      <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
      <div className="mt-5 space-y-3">
        {rows.length === 0 && <div className="text-xs text-slate-400 py-4">No data yet</div>}
        {rows.map((r) => {
          const label = keyFormatter ? keyFormatter(r.key) : r.key;
          const pct = (r.count / max) * 100;
          return (
            <div key={String(r.key)} className="flex items-center gap-3">
              <div className="w-24 text-xs text-[#0B192C] font-medium capitalize truncate">{label}</div>
              <div className="flex-1 bg-slate-100 h-2 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-[#FF5238]"
                  style={{ width: `${pct}%`, transition: "width 0.5s ease" }}
                />
              </div>
              <div className="w-8 text-right text-xs font-heading font-semibold text-[#0B192C]">
                {r.count}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
